import { useState, useRef, useCallback } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface PullToRefreshProps {
  onRefresh: () => Promise<void> | void;
  children: React.ReactNode;
  className?: string;
}

const PULL_THRESHOLD = 80;
const MAX_PULL = 120;

export const PullToRefresh = ({ onRefresh, children, className }: PullToRefreshProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  
  const pullDistance = useMotionValue(0);
  const indicatorOpacity = useTransform(pullDistance, [0, PULL_THRESHOLD], [0, 1]);
  const indicatorScale = useTransform(pullDistance, [0, PULL_THRESHOLD], [0.5, 1]);
  const rotation = useTransform(pullDistance, [0, PULL_THRESHOLD], [0, 180]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isRefreshing) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    // Check if we're at the top with a small tolerance for momentum
    const isAtTop = container.scrollTop <= 1;
    if (!isAtTop) return;
    
    startY.current = e.touches[0].clientY;
    currentY.current = e.touches[0].clientY;
    setIsPulling(true);
  }, [isRefreshing]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling || isRefreshing) return;
    
    const container = containerRef.current;
    if (!container) return;
    
    // If user scrolled down, cancel pull-to-refresh
    if (container.scrollTop > 1) {
      setIsPulling(false);
      animate(pullDistance, 0, { duration: 0.15 });
      return;
    }
    
    const touchY = e.touches[0].clientY;
    const diff = touchY - startY.current;
    
    // Only pull down, not up
    if (diff <= 0) {
      pullDistance.set(0);
      return;
    }
    
    currentY.current = touchY;
    const dampedDiff = Math.min(MAX_PULL, diff * 0.4);
    pullDistance.set(dampedDiff);
  }, [isPulling, isRefreshing, pullDistance]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) {
      // Ensure cleanup even if isPulling is already false
      animate(pullDistance, 0, { duration: 0.15 });
      return;
    }
    
    setIsPulling(false);
    const distance = pullDistance.get();
    
    if (distance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      animate(pullDistance, PULL_THRESHOLD * 0.6, { duration: 0.2 });
      
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        animate(pullDistance, 0, { duration: 0.3 });
      }
    } else {
      animate(pullDistance, 0, { duration: 0.3 });
    }
  }, [isPulling, isRefreshing, onRefresh, pullDistance]);

  const contentY = useTransform(pullDistance, (v) => v * 0.3);
  const indicatorTop = useTransform(pullDistance, (v) => v - 40);

  return (
    <div className={cn("relative h-full", className)}>
      {/* Pull indicator - rendered outside scroll container to avoid layout thrash */}
      <motion.div 
        className="absolute left-1/2 -translate-x-1/2 z-10 flex items-center justify-center pointer-events-none"
        style={{ 
          top: indicatorTop,
          opacity: indicatorOpacity,
          scale: indicatorScale
        }}
      >
        <motion.div 
          className="bg-primary text-primary-foreground rounded-full p-2 shadow-lg"
          style={{ rotate: isRefreshing ? undefined : rotation }}
          animate={isRefreshing ? { rotate: 360 } : undefined}
          transition={isRefreshing ? { duration: 1, repeat: Infinity, ease: "linear" } : undefined}
        >
          <Loader2 className="h-5 w-5" />
        </motion.div>
      </motion.div>

      {/* Scroll container — native scroll, no overflow on motion wrapper */}
      <div
        ref={containerRef}
        className="h-full overflow-y-auto overscroll-contain"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Inner wrapper handles the pull-down translate so scroll position is unaffected */}
        <motion.div style={{ y: contentY }}>
          {children}
        </motion.div>
      </div>
    </div>
  );
};
